import { Card, CardContent, Typography, useTheme } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HorizontalBarChart, ITssDashboardFilters } from 'reports/components';
import { CHART_COLORS } from 'reports/constants';
import { useGetBomHeader } from 'reports/hooks';
import { IBomHeaderJob } from 'reports/types';
import { Level, ModeType, Variant } from 'theme/enums';

interface IProps {
  title: string;
  currentYearStart: string;
  currentYearEnd: string;
  previousYearStart: string;
  previousYearEnd: string;
  filters?: ITssDashboardFilters;
  height: number | string;
}

export const JobsByTheatreChart = ({
  title,
  currentYearStart,
  currentYearEnd,
  previousYearStart,
  previousYearEnd,
  filters,
  height,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === ModeType.DARK;
  const previousYearColor = isDarkMode
    ? CHART_COLORS.previousYear.dark
    : CHART_COLORS.previousYear.light;

  const currentYearPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: currentYearStart,
        jobCreatedAtUntil: currentYearEnd,
      },
    }),
    [currentYearStart, currentYearEnd, filters]
  );

  const previousYearPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: previousYearStart,
        jobCreatedAtUntil: previousYearEnd,
      },
    }),
    [previousYearStart, previousYearEnd, filters]
  );

  const {
    records: currentYearRecords,
    isLoading: isLoadingCurrentYear,
    isFetching: isFetchingCurrentYear,
  } = useGetBomHeader(currentYearPayload);
  const {
    records: previousYearRecords,
    isLoading: isLoadingPreviousYear,
    isFetching: isFetchingPreviousYear,
  } = useGetBomHeader(previousYearPayload);

  const isLoading =
    isLoadingCurrentYear ||
    isLoadingPreviousYear ||
    isFetchingCurrentYear ||
    isFetchingPreviousYear;

  const transformedData = useMemo(() => {
    if (!currentYearRecords.length && !previousYearRecords.length) {
      return { datasets: [], labels: [] };
    }

    const theaterCounts: Record<string, { currentYear: number; previousYear: number }> = {};

    countJobsByTheatre(previousYearRecords, theaterCounts, 'previousYear', t('labelUnknown'));
    countJobsByTheatre(currentYearRecords, theaterCounts, 'currentYear', t('labelUnknown'));

    const sortedTheaters = Object.entries(theaterCounts)
      .sort(
        ([, theaterACounts], [, theaterBCounts]) =>
          theaterACounts.currentYear +
          theaterACounts.previousYear -
          (theaterBCounts.currentYear + theaterBCounts.previousYear)
      )
      .reverse();

    const labels = sortedTheaters.map(([name]) => name);
    const previousYearData = sortedTheaters.map(([, counts]) => counts.previousYear);
    const currentYearData = sortedTheaters.map(([, counts]) => counts.currentYear);

    return {
      datasets: [
        {
          backgroundColor: previousYearColor,
          data: previousYearData,
          label: t('labelPreviousYearCases'),
        },
        {
          backgroundColor: CHART_COLORS.currentYear,
          data: currentYearData,
          label: t('labelCurrentYearCases'),
        },
      ],
      labels,
    };
  }, [currentYearRecords, previousYearColor, previousYearRecords, t]);

  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      scales: {
        x: {
          stacked: false,
        },
        y: {
          stacked: false,
        },
      },
    }),
    []
  );

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <HorizontalBarChart
          data={transformedData}
          isLoading={isLoading}
          height={height}
          options={chartOptions}
        />
      </CardContent>
    </Card>
  );
};

const countJobsByTheatre = (
  jobs: IBomHeaderJob[],
  counts: Record<string, { currentYear: number; previousYear: number }>,
  key: keyof Record<string, { currentYear: number; previousYear: number }>,
  unknownLabel: string
) => {
  jobs.forEach((job) => {
    const theatreName = job.theatreName || unknownLabel;

    if (!counts[theatreName]) {
      counts[theatreName] = { currentYear: 0, previousYear: 0 };
    }

    counts[theatreName][key]++;
  });
};
